import { useState } from "react";
import { Link } from "react-router";
import type { AiMode } from "../../application/ports/settings.ts";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useSettingsState, useStorageStatus } from "../hooks/bindings.ts";
import {
  backupRestoreSummary,
  exportVocabulary,
  prepareBackup,
  restoreVocabulary,
} from "../../application/backup/backup.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import styles from "../design-system/ui.module.css";

export function SettingsScreen() {
  const { settingsController, platform, store } = useAppServices();
  const state = useSettingsState();
  const storageStatus = useStorageStatus();
  const preMigrationCopy =
    (storageStatus.kind === "ready" || storageStatus.kind === "empty") && storageStatus.preMigrationCopy
      ? storageStatus.preMigrationCopy
      : undefined;
  const [apiKey, setApiKey] = useState("");
  const [mode, setMode] = useState(state.settings.aiMode);
  const [httpBaseUrl, setHttpBaseUrl] = useState(state.settings.httpBaseUrl ?? "");
  const [message, setMessage] = useState<string>();
  const [hintDismissed, setHintDismissed] = useState(Boolean(state.settings.installHintDismissed));

  async function persistGenerator(nextMode: AiMode, url = httpBaseUrl) {
    setMode(nextMode);
    await settingsController.saveMode({
      ...state.settings,
      aiMode: nextMode,
      httpBaseUrl: url.trim() || undefined,
    });
  }

  async function dismissInstallHint() {
    setHintDismissed(true);
    await settingsController.saveMode({
      ...state.settings,
      aiMode: mode,
      httpBaseUrl: httpBaseUrl.trim() || undefined,
      installHintDismissed: true,
    });
  }

  const status = state.message ?? message;

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar title="Settings" backTo="/" />
        <header className={styles.pageHead}>
          <h1>Settings</h1>
          <p>Keys and backups stay on this device. Vocably has no account and no server of its own.</p>
        </header>
        <section className={styles.detail}>
          <h2>Gemini API key</h2>
          <p>
            The key is stored only in this browser. Scripts on this same site can read it. It is not a hidden server
            secret. Remote tracing stays off.
          </p>
          <p className={styles.countLabel}>{state.hasKey ? "A key is saved on this device." : "No key saved yet."}</p>
          <label className={styles.field}>
            API key
            <input
              className={styles.input}
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(event) => setApiKey(event.target.value)}
              placeholder="Paste your Gemini API key"
            />
          </label>
          <div className={styles.stack}>
            <PrimaryButton disabled={state.busy} onClick={() => void settingsController.saveKey(apiKey)}>
              Save key
            </PrimaryButton>
            <PrimaryButton secondary disabled={state.busy} onClick={() => void settingsController.test(apiKey)}>
              Test key
            </PrimaryButton>
            <PrimaryButton secondary disabled={state.busy} onClick={() => void settingsController.removeKey()}>
              Remove key
            </PrimaryButton>
          </div>
        </section>
        <label className={styles.field}>
          Generator
          <select
            className={styles.select}
            value={mode}
            onChange={(event) => void persistGenerator(event.target.value as AiMode)}
          >
            <option value="gemini">Gemini</option>
            <option value="sample">Sample generator (no API key)</option>
            <option value="http">Future HTTP adapter</option>
          </select>
        </label>
        {mode === "sample" ? (
          <p className={styles.settingsNote}>
            Sample generator is on. You can add words like “charge” without a Gemini key.
          </p>
        ) : null}
        {mode === "http" ? (
          <label className={styles.field}>
            HTTP adapter URL
            <input
              className={styles.input}
              value={httpBaseUrl}
              onChange={(event) => setHttpBaseUrl(event.target.value)}
              placeholder="http://127.0.0.1:8787"
            />
          </label>
        ) : null}
        {mode === "http" ? (
          <PrimaryButton secondary onClick={() => void persistGenerator(mode)}>
            Save adapter URL
          </PrimaryButton>
        ) : null}
        <section className={styles.detail} style={{ marginTop: 22 }}>
          <h2>Meaning cache</h2>
          <p>
            Looked-up meaning lists are stored on this device, separate from your saved words. They are not included in
            backups. Clearing the cache does not delete vocabulary.
          </p>
          <div className={styles.stack} style={{ marginTop: 12 }}>
            <PrimaryButton
              secondary
              onClick={() => {
                if (!window.confirm("Clear cached meaning lists? Vocably will ask the generator again the next time you look up a word.")) {
                  return;
                }
                void settingsController.clearDiscoverCache();
              }}
            >
              Clear cache
            </PrimaryButton>
          </div>
        </section>
        <section className={styles.detail} style={{ marginTop: 22 }}>
          <h2>Backup</h2>
          <p>Export or restore all local vocabulary. API keys are never included.</p>
          <div className={styles.stack} style={{ marginTop: 12 }}>
            <PrimaryButton
              secondary
              onClick={() => {
                const snapshot = store.getSnapshot();
                if (snapshot) {
                  void exportVocabulary(store, platform.download, platform.clock.now());
                }
              }}
            >
              Export backup
            </PrimaryButton>
            <label className={styles.field}>
              Restore backup
              <input
                className={styles.input}
                type="file"
                accept="application/json"
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }
                  const text = await file.text();
                  try {
                    const prepared = prepareBackup(text, { migratedAt: platform.clock.now() });
                    if (!window.confirm(backupRestoreSummary(prepared.report))) {
                      return;
                    }
                    const result = await restoreVocabulary(store, prepared.snapshot, {
                      expectedRevision: store.getSnapshot()?.dataRevision ?? 0,
                    });
                    setMessage(result.ok ? "Backup restored." : result.message);
                  } catch (error) {
                    setMessage(error instanceof Error ? error.message : "Restore failed.");
                  }
                }}
              />
            </label>
          </div>
        </section>
        {preMigrationCopy ? (
          <section className={styles.detail} style={{ marginTop: 22 }}>
            <h2>Pre-migration copy</h2>
            <p>
              Vocably kept one local copy from a previous vocabulary upgrade. This is a recovery aid on this
              device, not a substitute for a downloaded backup.
            </p>
            <div className={styles.stack} style={{ marginTop: 12 }}>
              <PrimaryButton
                secondary
                onClick={() => {
                  void store.exportPreMigrationCopy().then((bytes) => {
                    if (bytes == null) {
                      setMessage("There is no pre-migration copy to export.");
                      return;
                    }
                    platform.download.save("vocably-pre-migration.json", bytes, "application/json");
                  });
                }}
              >
                Export pre-migration copy
              </PrimaryButton>
              <PrimaryButton
                secondary
                onClick={() => {
                  if (
                    !window.confirm(
                      "Remove the local pre-migration copy? This does not delete your current vocabulary.",
                    )
                  ) {
                    return;
                  }
                  void store.removePreMigrationCopy().then((result) => {
                    setMessage(result.ok ? "Pre-migration copy removed." : result.message);
                  });
                }}
              >
                Remove pre-migration copy
              </PrimaryButton>
            </div>
          </section>
        ) : null}
        <p className={styles.countLabel}>
          <Link to="/import">Import words from CSV</Link>
        </p>
        {!hintDismissed ? (
          <div className={styles.banner}>
            <div>
              Add to Home Screen on iPhone: Share → Add to Home Screen. That is separate from the offline cache.
              <div>
                <button className={styles.textButton} type="button" onClick={() => void dismissInstallHint()}>
                  Got it
                </button>
              </div>
            </div>
          </div>
        ) : null}
        {status ? (
          <p className={`${styles.banner} ${/saved|works|removed|restored|Preferences|cleared/i.test(status) ? styles.bannerSuccess : ""}`}>
            {status}
          </p>
        ) : null}
      </Screen>
    </Frame>
  );
}
